/**
 * Event Visibility Filter
 *
 * Compact two-row filter:
 * - Row 1: All | RESTRICTED | MINISTERS
 * - Row 2: Archive | CHURCH | PUBLIC
 */

import { useEffect, useRef } from "react";
import {
  IconGlobe,
  IconUsers,
  IconShield,
  IconLock,
  IconLayers,
  IconArchive,
} from "../../../components/Icons.tsx";
import { getTelegram } from "../../../lib/telegram.ts";
import type { EventVisibility } from "./types.ts";

export type EventVisibilityFilter = "all" | EventVisibility | "archived";

export interface EventVisibilitySliderProps {
  value: EventVisibilityFilter;
  onChange: (status: EventVisibilityFilter) => void;
  counts?: Partial<Record<EventVisibilityFilter, number>>;
  className?: string;
}

interface FilterOption {
  status: EventVisibilityFilter;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  activeClass: string;
  inactiveClass: string;
}

const ROW_1: FilterOption[] = [
  {
    status: "all",
    Icon: IconLayers,
    label: "Все",
    activeClass: "bg-slate-700 text-white",
    inactiveClass: "bg-slate-100 text-slate-600",
  },
  {
    status: "RESTRICTED",
    Icon: IconLock,
    label: "Огран.",
    activeClass: "bg-amber-500 text-white",
    inactiveClass: "bg-amber-50 text-amber-700",
  },
  {
    status: "MINISTERS",
    Icon: IconShield,
    label: "Служ.",
    activeClass: "bg-purple-500 text-white",
    inactiveClass: "bg-purple-50 text-purple-700",
  },
];

const ROW_2: FilterOption[] = [
  {
    status: "archived",
    Icon: IconArchive,
    label: "Архив",
    activeClass: "bg-red-500 text-white",
    inactiveClass: "bg-slate-100 text-slate-600",
  },
  {
    status: "CHURCH",
    Icon: IconUsers,
    label: "Церковь",
    activeClass: "bg-blue-500 text-white",
    inactiveClass: "bg-blue-50 text-blue-700",
  },
  {
    status: "PUBLIC",
    Icon: IconGlobe,
    label: "Публ.",
    activeClass: "bg-emerald-500 text-white",
    inactiveClass: "bg-emerald-50 text-emerald-700",
  },
];

function triggerHaptic() {
  const tg = getTelegram();
  if (tg?.HapticFeedback) {
    tg.HapticFeedback.impactOccurred("light");
  } else if ("vibrate" in navigator) {
    navigator.vibrate(10);
  }
}

export function EventVisibilitySlider({
  value,
  onChange,
  counts,
  className = "",
}: EventVisibilitySliderProps) {
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      triggerHaptic();
      prevValueRef.current = value;
    }
  }, [value]);

  const handleSelect = (status: EventVisibilityFilter) => {
    if (status !== value) onChange(status);
  };

  const renderRow = (options: FilterOption[]) => (
    <div className="flex gap-1.5">
      {options.map((opt) => {
        const isActive = value === opt.status;
        const count = counts?.[opt.status] ?? 0;

        return (
          <button
            key={opt.status}
            type="button"
            onClick={() => {
              triggerHaptic();
              handleSelect(opt.status);
            }}
            className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all active:scale-[0.97] ${
              isActive ? opt.activeClass : opt.inactiveClass
            }`}
          >
            <opt.Icon className="w-3.5 h-3.5" />
            <span>{opt.label}</span>
            {count > 0 && (
              <span
                className={`text-[10px] ${isActive ? "opacity-80" : "opacity-60"}`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={`space-y-1.5 ${className}`}>
      {renderRow(ROW_1)}
      {renderRow(ROW_2)}
    </div>
  );
}
